import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Checkbox } from "./ui/checkbox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-US", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }) : "never");
const KIND_LABEL = { reminder_nudge: "Reminder nudge", weekly_digest: "Weekly digest", cadence_t2: "Cadence T-2 (owner)", cadence_due: "Cadence due today (owner)", cadence_overdue: "Cadence overdue (owner)", cadence_team_overdue: "Cadence overdue (team escalation)" };

export default function EmailCenter({ active }) {
  const [settings, setSettings] = useState(null);
  const [outbox, setOutbox] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    axios.get(`${API}/email-settings`).then((r) => setSettings(r.data)).catch(() => {});
    axios.get(`${API}/emails?limit=50`).then((r) => setOutbox(r.data)).catch(() => setOutbox([]));
  }, []);
  useEffect(() => { if (active === "mail") load(); }, [active, load]);

  const toggle = async (field, value) => {
    const next = { daily_reminders_enabled: settings.daily_reminders_enabled, weekly_digest_enabled: settings.weekly_digest_enabled, [field]: value };
    try {
      const res = await axios.put(`${API}/email-settings`, next);
      setSettings(res.data);
      toast.success("Email settings saved");
    } catch {
      toast.error("Could not save settings");
    }
  };

  const sendReminders = async () => {
    setBusy("rem");
    try {
      const res = await axios.post(`${API}/emails/send-reminders`);
      if (res.data.sent === 0) toast.info(res.data.message || "Nothing due — no nudge sent");
      else toast.success(`Nudge ${res.data.mode === "PREVIEW" ? "previewed" : "sent"} to ${res.data.recipients} member${res.data.recipients === 1 ? "" : "s"} (${res.data.items} items)`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    }
    setBusy("");
  };

  const sendDigest = async () => {
    setBusy("dig");
    try {
      const res = await axios.post(`${API}/emails/send-digest`);
      toast.success(`Your digest was ${res.data.status === "PREVIEW" ? "generated in preview mode" : "sent"} — see the outbox below`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    }
    setBusy("");
  };

  return (
    <section className={`chapter utility${active === "mail" ? " active" : ""}`} id="mail">
      <div className="eyebrow">Working record · the reminder engine's outbound channel — daily nudges and Monday digests</div>
      <h1>Email Center</h1>
      <p className="lede">The reminders bar becomes email: a daily 8:00 AM ET nudge to every team member when statutory windows come due, the monthly-cadence schedule per deadline (T-2 to the owner · due-date to the owner · overdue daily to owner + full team), and a personal Monday-morning digest. Every email lands in the outbox below — in preview mode it is stored, <b>not sent</b>.</p>

      {settings && (
        <div className="formcard" data-testid="mail-settings">
          <div className="formcard-title">
            Delivery mode:{" "}
            <span className={`vr-pill ${settings.mode === "LIVE" ? "cleared" : "open"}`} data-testid="mail-mode-badge">{settings.mode}</span>
          </div>
          {settings.mode === "PREVIEW" ? (
            <>
              <div className="pc-decision decline" data-testid="mail-not-live-warning" style={{ marginBottom: 12 }}>
                EMAILS ARE NOT BEING DELIVERED. No Resend API key is on file — every email below is generated and stored in the outbox only. Nothing reaches an inbox until <span className="q">RESEND_API_KEY</span> is added to the backend environment.
              </div>
              <p className="k-sub" style={{ marginBottom: 12 }}>
                To go live: add a Resend API key (free at resend.com) as <span className="q">RESEND_API_KEY</span>; delivery switches on automatically — no code change.
              </p>
            </>
          ) : (
            <p className="k-sub" style={{ marginBottom: 12 }}>Sending live via Resend from <span className="q">{settings.sender}</span>.</p>
          )}
          <label className="agree-row" style={{ marginBottom: 8 }}>
            <Checkbox checked={settings.daily_reminders_enabled} onCheckedChange={(v) => toggle("daily_reminders_enabled", !!v)} data-testid="mail-toggle-daily" />
            <span>Daily reminder nudge to all team members — 8:00 AM ET, only when something is due or overdue</span>
          </label>
          <label className="agree-row" style={{ marginBottom: 12 }}>
            <Checkbox checked={settings.weekly_digest_enabled} onCheckedChange={(v) => toggle("weekly_digest_enabled", !!v)} data-testid="mail-toggle-weekly" />
            <span>Monday digest to each team member — 8:05 AM ET, their week's overdue items and deadlines</span>
          </label>
          <div className="vr-btnrow">
            <button className="btn" onClick={sendReminders} disabled={!!busy} data-testid="mail-send-reminders">{busy === "rem" ? "Sending…" : "Send reminder nudges now"}</button>
            <button className="btn ghost" onClick={sendDigest} disabled={!!busy} data-testid="mail-send-digest">{busy === "dig" ? "Sending…" : "Send me my digest now"}</button>
          </div>
          <p className="k-sub" style={{ marginTop: 10 }}>
            Last nudge run: {fmt(settings.last_reminder_run)} · Last digest run: {fmt(settings.last_digest_run)}
          </p>
        </div>
      )}

      <h2>Outbox {outbox ? `— last ${outbox.length}` : ""}</h2>
      <table data-testid="mail-outbox-table">
        <tbody>
          <tr><th>When</th><th>To</th><th>Kind</th><th>Subject</th><th>Status</th><th></th></tr>
          {outbox === null ? (
            <tr><td colSpan={6}>Loading…</td></tr>
          ) : outbox.length === 0 ? (
            <tr><td colSpan={6}>Nothing sent yet — trigger a nudge or digest above.</td></tr>
          ) : (
            outbox.map((m) => (
              <tr key={m.id}>
                <td>{fmt(m.created_at)}</td>
                <td>{m.to}</td>
                <td>{KIND_LABEL[m.kind] || m.kind}</td>
                <td>{m.subject}</td>
                <td><span className={`vr-pill ${m.status === "SENT" ? "cleared" : m.status === "FAILED" ? "declined" : "open"}`}>{m.status}</span></td>
                <td><button className="vr-link" onClick={() => setPreview(preview === m.id ? null : m.id)} data-testid={`mail-preview-${m.id}`}>{preview === m.id ? "Hide" : "Preview"}</button></td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      {preview && outbox && (
        <div className="mail-preview" data-testid="mail-preview-body"
          dangerouslySetInnerHTML={{ __html: outbox.find((m) => m.id === preview)?.html || "" }} />
      )}
      <p style={{ color: "var(--mute)", fontSize: "13px" }}>Emails carry no PHI — reminders reference claim and case IDs only (SOP 6.2 minimum necessary).</p>
    </section>
  );
}
